import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { publicApi } from '../lib/api';
import { toast } from 'sonner';
import { useLanguage } from '../hooks/useLanguage';
import SEO from '../components/SEO';

const ContactPage = () => {
  const { t } = useLanguage();
  
  const services = [
    t('services.trademarks.title'),
    t('services.businessFormation.title'),
    t('services.contracts.title'),
    t('services.governmentContracts.title'),
    t('services.personalInjury.title'),
  ];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_interest: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'contact' }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await publicApi.createLead({
        ...formData,
        source: 'contact_form',
      });
      setSubmitted(true);
      toast.success('Message sent successfully!');
      publicApi.trackEvent({ 
        event_type: 'form_submit', 
        page: 'contact',
        metadata: { service_interest: formData.service_interest }
      }).catch(() => {});
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-page">
        <SEO 
          title="Contact Us"
          description="Contact J. Brantley Law, PLLC for legal assistance with trademarks, business formation, and contracts. Schedule a consultation today."
        />
        <section className="bg-[#FDFCFA] min-h-[60vh] flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
            <div className="max-w-xl mx-auto">
              <div className="w-16 h-16 bg-[#1F2A44] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl font-serif text-[#222222] mb-4">
                {t('contact.successTitle')}
              </h1>
              <p className="text-lg text-[#555555] mb-8">
                {t('contact.successDesc')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/book">
                  <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-8 py-4 rounded-sm">
                    {t('nav.bookConsultation')}
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="border-[#1F2A44] text-[#1F2A44] px-8 py-4 rounded-sm">
                    {t('contact.returnHome')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
                {t('contact.sectionLabel')}
              </p>
              <h1 className="text-4xl sm:text-5xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
                {t('contact.headline')}
              </h1>
              <p className="text-lg text-[#555555] leading-relaxed mb-8">
                {t('contact.description')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#1F2A44] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#222222] mb-1">{t('contact.phoneLabel')}</h3>
                    <a 
                      href="tel:+12107422435"
                      className="text-[#1F2A44] hover:underline"
                    >
                      (210) 742-2435
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#1F2A44] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#222222] mb-1">{t('contact.emailLabel')}</h3>
                    <a 
                      href="mailto:jbrantley@jenniferbrantleylaw.com"
                      className="text-[#1F2A44] hover:underline"
                    >
                      jbrantley@jenniferbrantleylaw.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#1F2A44] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#222222] mb-1">{t('contact.locationLabel')}</h3>
                    <p className="text-[#555555]">{t('contact.location')}</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-[#F5F3EF] border border-[#E8E4DC]">
                <h3 className="font-semibold text-[#222222] mb-2">{t('contact.preferDirectTitle')}</h3>
                <p className="text-sm text-[#555555] mb-4">
                  {t('contact.preferDirectDesc')}
                </p>
                <Link to="/book">
                  <Button className="bg-[#1F2A44] hover:bg-[#151D30] text-white px-6 py-2 rounded-sm text-sm">
                    {t('nav.bookConsultation')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Contact Form - PracticePanther Embed */}
            <div className="bg-white p-8 md:p-10 border border-[#E8E4DC] shadow-lg">
              <h2 className="text-2xl font-serif text-[#222222] mb-6">{t('contact.formTitle')}</h2>
              <div className="w-full h-[1000px]">
                <iframe
                  src="https://app.practicepanther.com/IntakeForm/Create?templateGuid=1b8e4e34-b6dc-45ba-b039-e69dddab0a1f&isEmbed=true"
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                  title="Contact Form"
                  className="rounded-sm"
                  data-testid="contact-form-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-white border-t border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <p className="text-xs text-[#555555] text-center">
            {t('contact.disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
